import type { WheelSegment } from './wheelTypes';

const RES = 1000;
const CX = 500;
const CY = 500;
const R_OUTER = 498;
const R_INNER = 176;
const R_LABEL = 312;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgb(r: number, g: number, b: number, a = 1): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  if (a < 1) return `rgba(${clamp(r)},${clamp(g)},${clamp(b)},${a})`;
  return `rgb(${clamp(r)},${clamp(g)},${clamp(b)})`;
}

function mix(hex: string, other: string, t: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(other);
  return rgb(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t);
}

function lighten(hex: string, t: number): string {
  return mix(hex, '#ffffff', t);
}

function darken(hex: string, t: number): string {
  return mix(hex, '#050203', t);
}

function toRad(deg: number): number {
  return ((deg - 90) * Math.PI) / 180;
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function slicePath(
  ctx: CanvasRenderingContext2D,
  r0: number,
  r1: number,
  a0: number,
  a1: number,
): void {
  ctx.beginPath();
  ctx.arc(CX, CY, r1, a0, a1, false);
  ctx.arc(CX, CY, r0, a1, a0, true);
  ctx.closePath();
}

function lighting(midDeg: number): number {
  const mid = toRad(midDeg);
  const lx = Math.cos(toRad(-38));
  const ly = Math.sin(toRad(-38));
  const nx = Math.cos(mid);
  const ny = Math.sin(mid);
  return 0.82 + 0.18 * (nx * lx + ny * ly);
}

function labelPalette(segment: WheelSegment): {
  stroke: string;
  depth: string;
  mid: string;
  hi: string;
  glow: string;
} {
  const lum = luminance(segment.fill);
  if (lum > 0.68) {
    return {
      stroke: '#5a0030',
      depth: '#c2185b',
      mid: '#ff2d7a',
      hi: '#ffd4ea',
      glow: 'rgba(255, 70, 150, 0.42)',
    };
  }
  if (segment.premium) {
    return {
      stroke: '#4a2200',
      depth: '#b86a00',
      mid: '#ffd24a',
      hi: '#fff6c4',
      glow: 'rgba(255, 210, 70, 0.5)',
    };
  }
  return {
    stroke: '#3a1600',
    depth: '#c06000',
    mid: '#ffe36a',
    hi: '#fff8d8',
    glow: 'rgba(255, 190, 60, 0.38)',
  };
}

export function drawPrizeDisc(canvas: HTMLCanvasElement, segments: WheelSegment[]): void {
  const dpr = Math.min(2.75, Math.max(1, window.devicePixelRatio || 1));
  canvas.width = Math.round(RES * dpr);
  canvas.height = Math.round(RES * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, RES, RES);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const slice = 360 / segments.length;

  segments.forEach((segment, index) => {
    const start = index * slice;
    const end = start + slice;
    const mid = start + slice / 2;
    const a0 = toRad(start);
    const a1 = toRad(end);
    const lit = lighting(mid);
    const lift = Math.max(0, lit - 0.82);
    const shade = Math.max(0, 0.82 - lit);

    const hotX = CX + Math.cos(toRad(mid - 14)) * 265;
    const hotY = CY + Math.sin(toRad(mid - 14)) * 265;
    const body = ctx.createRadialGradient(hotX, hotY, 6, CX, CY, R_OUTER);
    body.addColorStop(0, lighten(segment.highlight, 0.04 + lift * 0.1));
    body.addColorStop(0.22, segment.highlight);
    body.addColorStop(0.48, segment.fill);
    body.addColorStop(0.82, shade > 0 ? darken(segment.fillDeep, shade * 0.12) : segment.fillDeep);
    body.addColorStop(1, darken(segment.fillDeep, 0.22 + shade * 0.16));

    slicePath(ctx, R_INNER, R_OUTER, a0, a1);
    ctx.fillStyle = body;
    ctx.fill();

    const bevel = ctx.createLinearGradient(
      CX + Math.cos(a0) * 270,
      CY + Math.sin(a0) * 270,
      CX + Math.cos(a1) * 270,
      CY + Math.sin(a1) * 270,
    );
    bevel.addColorStop(0, rgb(255, 255, 255, 0.32 + lift * 0.12));
    bevel.addColorStop(0.18, rgb(255, 255, 255, 0.08));
    bevel.addColorStop(0.55, rgb(255, 255, 255, 0));
    bevel.addColorStop(1, rgb(40, 8, 6, 0.26 + shade * 0.12));
    slicePath(ctx, R_INNER + 3, R_OUTER - 2, a0, a1);
    ctx.fillStyle = bevel;
    ctx.fill();

    const candyX = CX + Math.cos(toRad(mid - 10)) * 228;
    const candyY = CY + Math.sin(toRad(mid - 10)) * 228;
    const gloss = ctx.createRadialGradient(candyX, candyY, 4, candyX, candyY, 150);
    gloss.addColorStop(0, rgb(255, 255, 255, 0.42 + lit * 0.08));
    gloss.addColorStop(0.28, rgb(255, 244, 200, 0.16));
    gloss.addColorStop(1, rgb(255, 255, 255, 0));
    slicePath(ctx, R_INNER + 12, R_OUTER - 10, a0, a1);
    ctx.fillStyle = gloss;
    ctx.fill();
  });

  segments.forEach((_, index) => {
    const ang = toRad(index * slice);
    ctx.beginPath();
    ctx.moveTo(CX + Math.cos(ang) * (R_INNER + 4), CY + Math.sin(ang) * (R_INNER + 4));
    ctx.lineTo(CX + Math.cos(ang) * R_OUTER, CY + Math.sin(ang) * R_OUTER);
    ctx.strokeStyle = 'rgba(255,255,255,0.98)';
    ctx.lineWidth = 6;
    ctx.stroke();
  });

  ctx.beginPath();
  ctx.arc(CX, CY, R_OUTER - 2, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(CX, CY, R_INNER + 2, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.86)';
  ctx.lineWidth = 10;
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.arc(CX, CY, R_OUTER - 4, 0, Math.PI * 2);
  ctx.arc(CX, CY, R_INNER + 8, 0, Math.PI * 2, true);
  ctx.clip();
  const sheen = ctx.createLinearGradient(CX - 240, CY - 300, CX + 210, CY + 260);
  sheen.addColorStop(0, rgb(255, 255, 255, 0.16));
  sheen.addColorStop(0.26, rgb(255, 255, 255, 0.05));
  sheen.addColorStop(0.62, rgb(255, 255, 255, 0));
  sheen.addColorStop(1, rgb(20, 8, 6, 0.1));
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, RES, RES);
  const spec = ctx.createRadialGradient(CX - 140, CY - 180, 6, CX - 90, CY - 120, 170);
  spec.addColorStop(0, rgb(255, 255, 255, 0.2));
  spec.addColorStop(1, rgb(255, 255, 255, 0));
  ctx.fillStyle = spec;
  ctx.fillRect(0, 0, RES, RES);
  ctx.restore();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  segments.forEach((segment, index) => {
    const mid = index * slice + slice / 2;
    const a = toRad(mid);
    const lines = segment.label.split(' ');
    const fontSize = segment.premium ? 42 : 38;
    const palette = labelPalette(segment);
    ctx.save();
    ctx.translate(CX + Math.cos(a) * R_LABEL, CY + Math.sin(a) * R_LABEL);
    ctx.rotate(a + Math.PI / 2);
    ctx.font = `900 ${fontSize}px Manrope, Segoe UI, sans-serif`;
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    const lineH = fontSize * 0.94;
    const originY = -((lines.length - 1) * lineH) / 2;
    lines.forEach((line, lineIndex) => {
      const y = originY + lineIndex * lineH;
      ctx.shadowColor = palette.glow;
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 2;
      ctx.strokeStyle = palette.stroke;
      ctx.lineWidth = 7.2;
      ctx.strokeText(line, 0, y);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = palette.depth;
      ctx.fillText(line, 1.6, y + 2.4);
      ctx.fillText(line, 0.8, y + 1.2);
      const face = ctx.createLinearGradient(0, y - fontSize * 0.42, 0, y + fontSize * 0.42);
      face.addColorStop(0, palette.hi);
      face.addColorStop(0.42, palette.mid);
      face.addColorStop(1, darken(palette.mid, 0.12));
      ctx.fillStyle = face;
      ctx.fillText(line, 0, y);
      ctx.strokeStyle = lighten(palette.hi, 0.15);
      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 1.35;
      ctx.strokeText(line, 0, y - 0.8);
      ctx.globalAlpha = 1;
    });
    ctx.restore();
  });
}

export async function paintPrizeDisc(canvas: HTMLCanvasElement, segments: WheelSegment[]): Promise<void> {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    // keep going with fallback fonts
  }
  drawPrizeDisc(canvas, segments);
}
